import { BillingAlertProduct } from '../../../../typings/BillingAlert.interface';
import { stringifyQuery } from '../utils';

const appendFilters = (key: string, filters?: object) => {
  if (!filters) return key;
  return `${key}?${stringifyQuery(filters)}`;
};

const APIMethodKeys = {
  outpost: {
    get: () => '/outpost',
    getConfig: () => '/outpost/config',
    getApiKey: () => '/outpost/api_key',
    getCustomDomain: () => '/outpost/config/custom_domain',
    getStatus: () => '/outpost/status',
    tenants: {
      list: (filters?: object) => appendFilters('/outpost/tenants', filters),
      get: (id: string) => `/outpost/${id}`,
    },
    destinations: {
      list: (tenant_id: string, filters?: object) =>
        appendFilters(`/outpost/${tenant_id}/destinations`, filters),
      get: (tenant_id: string, destination_id: string) =>
        `/outpost/${tenant_id}/destinations/${destination_id}`,
    },
    destinationTypes: {
      list: () => `/outpost/destination-types`,
      get: (type: string) => `/outpost/destination-types/${type}`,
    },
    events: {
      list: (filters?: object) => appendFilters('/outpost/events', filters),
      get: (event_id: string) => `/outpost/events/${event_id}`,
    },
    attempts: {
      list: (filters?: object) => appendFilters('/outpost/attempts', filters),
      get: (attempt_id: string, options?: { include?: string[] }) =>
        appendFilters(`/outpost/attempts/${attempt_id}`, options),
    },
    operatorEvents: {
      destinationTypes: {
        list: () => '/outpost/operator-events/destination-types',
      },
      destinations: {
        list: () => '/outpost/operator-events/destinations',
        get: (destination_id: string) => `/outpost/operator-events/destinations/${destination_id}`,
      },
      events: {
        list: (filters?: object) => appendFilters('/outpost/operator-events/events', filters),
        get: (event_id: string) => `/outpost/operator-events/events/${event_id}`,
        listAttempts: (event_id: string) => `/outpost/operator-events/events/${event_id}/attempts`,
      },
      attempts: {
        list: (filters?: object) => appendFilters('/outpost/operator-events/attempts', filters),
        get: (attempt_id: string, options?: { include?: string[] }) =>
          appendFilters(`/outpost/operator-events/attempts/${attempt_id}`, options),
      },
    },
  },
  billing: {
    getAlert: (product: BillingAlertProduct): string => `/internal/billing/alerts/${product}`,
    getThroughputTiers: (): string => '/internal/throughput/tiers',
    getUsage: (filters?: object): string => appendFilters('/internal/billing/usage', filters),
    getPlans: (filters?: object): string => appendFilters('/internal/billing/plans', filters),
    getOutpostPlans: (filters?: object): string =>
      appendFilters('/internal/billing/outpost/plans', filters),
    getCard: (): string => `/internal/billing/card`,
    getInvoices: (): string => `/internal/billing/invoices`,
    getBillingEmail: (): string => `/internal/billing/email`,
    getBillingAddress: (): string => `/internal/billing/address`,
    getSubscriptionDetails: (): string => `/internal/billing/subscription/details`,
    getOutpostSubscriptionDetails: (): string => `/internal/billing/outpost/subscription/details`,
    getOutpostStaticIPs: (): string => `/internal/billing/outpost/static_ips`,
    // Generic addon methods
    getAddons: (): string => `/internal/billing/addons`,
    getAddonPrice: (addon_key: string): string => `/internal/billing/addons/${addon_key}/price`,
  },
  webhooks: {
    count: (filters?: object): string => appendFilters('/webhooks/count', filters),
    get: (id: string): string => appendFilters(`/webhooks/${id}`),
    list: (filters?: object): string => appendFilters('/webhooks', filters),
  },
  attempts: {
    get: (id: string): string => appendFilters(`/attempts/${id}`),
    list: (filters?: object): string => appendFilters('/attempts', filters),
  },
  destinations: {
    get: (id: string, filters?: object): string => appendFilters(`/destinations/${id}`, filters),
    list: (filters?: object): string => appendFilters('/destinations', filters),
    count: (filters?: object): string => appendFilters('/destinations/count', filters),
  },
  destinationTypes: {
    list: () => '/destination-types',
  },
  events: {
    get: (id: string, options?: { event_request?: 'original' }): string =>
      appendFilters(`/events/${id}`, options),
    getRawBody: (id: string): string => `/events/${id}/raw_body`,
    list: (filters?: object): string => appendFilters('/events', filters),
  },
  ignored_events: {
    list: (filters?: object): string => appendFilters('/ignored-events', filters),
  },
  requests: {
    get: (id: string, filters?: object): string => appendFilters(`/requests/${id}`, filters),
    getRawBody: (id: string): string => `/requests/${id}/raw_body`,
    list: (filters?: object): string => appendFilters('/requests', filters),
    countEvents: (id: string, filters?: object): string =>
      appendFilters(`/requests/${id}/events/count`, filters),
    listEvents: (id: string, filters?: object): string =>
      appendFilters(`/requests/${id}/events`, filters),
    listIgnoredEvents: (id: string, filters?: object): string =>
      appendFilters(`/requests/${id}/ignored_events`, filters),
  },
  bookmarks: {
    get: (id: string): string => appendFilters(`/bookmarks/${id}`),
    getRawBody: (id: string): string => `/bookmarks/${id}/raw_body`,
    list: (filters?: object): string => appendFilters('/bookmarks', filters),
  },
  sources: {
    get: (id: string, filters?: object): string => appendFilters(`/sources/${id}`, filters),
    list: (filters?: object): string => appendFilters('/sources', filters),
    count: (filters?: object): string => appendFilters('/sources/count', filters),
  },
  sourceTypes: {
    list: () => '/source-types',
  },
  issues: {
    get: (id: string): string => appendFilters(`/issues/${id}`),
    list: (filters?: object): string => appendFilters('/issues', filters),
    count: (filters?: object): string => appendFilters(`/issues/count`, filters),
  },
  issue_triggers: {
    get: (id: string): string => appendFilters(`/issue-triggers/${id}`),
    list: (filters?: object): string => appendFilters('/issue-triggers', filters),
    count: (filters?: object): string => appendFilters(`/issue-triggers/count`, filters),
  },
  team_integrations: {
    list: (filters?): string => appendFilters(`/session/team-integrations`, filters),
  },
  transformations: {
    list: (filters?): string => appendFilters(`/transformations`, filters),
    count: (filters?: object): string => appendFilters('/transformations/count', filters),
    get: (id: string): string => `/transformations/${id}`,
    listExecutions: (id, filters?: object): string =>
      appendFilters(`/transformations/${id}/executions`, filters),
    getExecution: (id, execution_id): string =>
      appendFilters(`/transformations/${id}/executions/${execution_id}`),
    hasConnections: (id: string): string => `/transformations/${id}/connections`,
    getConnections: (id: string): string => `/internal/transformations/${id}/connections`,
  },
  teams: {
    listAll: (): string => `/projects`,
    listCustomDomains: (id: string): string => `/projects/${id}/custom_domains`,
    listMembers: (id: string): string => `/projects/${id}/members`,
    listApiKeys: (id: string): string => `/projects/${id}/api-keys`,
  },
  organizations: {
    listAll: (): string => `/organizations`,
    listMembers: (id: string): string => `/organizations/${id}/members`,
    listMemberInvites: (id: string): string => `/organizations/${id}/invites`,
    validateInviteToken: (id: string): string => `/internal/validate_invite_token/${id}`,
    getSecurity: (id: string): string => `/organizations/${id}/security`,
    listApiKeys: (id: string): string => `/organizations/${id}/api-keys`,
  },
  bulk: {
    events: {
      count: (filters?: object): string => appendFilters('/bulk/events/count', filters),
      list: (filters?: object): string => appendFilters('/bulk/events', filters),
      get: (id: string): string => `/bulk/events/${id}`,
      plan: (filters?: object): string => appendFilters('/bulk/events/plan', filters),
    },
    cancelEvents: {
      list: (filters?: object): string => appendFilters('/bulk/events/cancel', filters),
      get: (id: string): string => `/bulk/events/cancel/${id}`,
      plan: (filters?: object): string => appendFilters('/bulk/events/cancel/plan', filters),
    },
    requests: {
      count: (filters?: object): string => appendFilters('/bulk/requests/count', filters),
      list: (filters?: object): string => appendFilters('/bulk/requests', filters),
      get: (id: string): string => `/bulk/requests/${id}`,
      plan: (filters?: object): string => appendFilters('/bulk/requests/plan', filters),
    },
    requestsReplay: {
      count: (filters?: object): string => appendFilters('/bulk/requests/replay/count', filters),
      list: (filters?: object): string => appendFilters('/bulk/requests/replay', filters),
      get: (id: string): string => `/bulk/requests/replay/${id}`,
      plan: (filters?: object): string => appendFilters('/bulk/requests/replay/plan', filters),
    },
    ignored_events: {
      count: (filters?: object): string => appendFilters('/bulk/ignored-events/count', filters),
      list: (filters?: object): string => appendFilters('/bulk/ignored-events', filters),
      get: (id: string): string => `/bulk/ignored-events/${id}`,
      plan: (filters?: object): string => appendFilters('/bulk/ignored-events/plan', filters),
    },
  },
  cli_clients: {
    list: (filters?: object): string => appendFilters('/cli', filters),
    getSessions: (filters?: object): string => appendFilters('/cli-sessions', filters),
  },
  session: {
    me: (): string => '/session/me',
    context: (): string => '/session/context',
    getCurrentSigningSecret: (): string => '/session/signing_secret',
    listCliKeys: (filters?: object): string => appendFilters('/session/me/cli_keys', filters),
    listSessions: (): string => '/session/me/sessions',
  },
  views: {
    list: (filters?: object): string => appendFilters('/views', filters),
  },
};

export default APIMethodKeys;
